<template>
  <div class="app-shell home-page">
    <div class="app-shell__main">
      <AppHeader />

      <main class="content">
        <section class="hero-card hero-card--home">
          <div class="hero-shell">
            <div class="hero-main">
              <h1>一个处理媒体素材的网站</h1>
              <p class="hero-copy">
                面向创作者、电商运营和内容编辑场景，把常用素材处理工具收进同一个入口。打开后直接开始，减少来回切站和重复学习。
              </p>

              <div class="hero-actions">
                <button type="button" class="primary-button" @click="handleFeatureClick(features[0])">先试链接去水印</button>
                <button type="button" class="secondary-button" @click="scrollToGrid">查看全部工具</button>
              </div>

              <div class="hero-proof">
                <article v-for="metric in metrics" :key="metric.label" class="proof-chip">
                  <strong>{{ metric.value }}</strong>
                  <span>{{ metric.label }}</span>
                </article>
              <div class="hero-badge">阿呆云媒体工具箱</div>
              </div>
            </div>

            <aside class="hero-stage">
              <div class="hero-stage__panel">
                <div class="hero-stage__eyebrow">当前可用能力</div>
                <div class="hero-stage__headline">{{ features.length }} 个核心入口</div>
                <p class="hero-stage__copy">从分享链接提取素材，到图片清理、本地视频去水印，再到主体抠图，都能在这里直接完成。</p>

                <div class="hero-stage__mini-grid">
                  <div v-for="point in heroPoints" :key="point.title" class="hero-stage__mini-card">
                    <strong>{{ point.title }}</strong>
                    <span>{{ point.description }}</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section ref="gridRef" class="card-panel feature-market feature-market--primary">
          <div class="panel-head panel-head--feature">
            <div class="section-kicker">核心入口</div>
            <h2 class="feature-market__title">4 个核心工具</h2>
            <p class="feature-market__subtitle">链接去水印、图片修复、本地视频去水印、主体抠图。</p>
          </div>

          <div class="feature-grid feature-grid--spotlight">
            <article v-for="feature in features" :key="feature.id" class="feature-card feature-card--spotlight" :class="`feature-card--${feature.tone}`">
              <div class="feature-card__topline">
                <span class="feature-card__tag">{{ feature.tag }}</span>
              </div>
              <div class="feature-card__logo">
                <div class="feature-card__icon" :class="`is-${feature.tone}`">
                  <div class="feature-card__icon-graphic" v-html="feature.iconSvg"></div>
                </div>
                <div class="feature-card__logo-copy">
                  <strong>{{ feature.logoTitle }}</strong>
                  <span>{{ feature.logoSubtitle }}</span>
                </div>
              </div>
              <div class="feature-card__copy">
                <h2 class="feature-card__title">{{ feature.title }}</h2>
                <p class="feature-card__description">{{ feature.description }}</p>
              </div>
              <div class="feature-card__footer">
                <span class="feature-card__scene">{{ feature.scene }}</span>
                <button type="button" class="feature-card__button feature-card__button--strong" @click="handleFeatureClick(feature)">
                  {{ feature.buttonText }}
                </button>
              </div>
            </article>
          </div>
        </section>

        <section class="flow-card scene-panel">
          <div class="panel-head panel-head--scene">
            <div class="section-kicker">适用场景</div>
            <div class="section-title">更适合内容处理</div>
            <p class="scene-panel__subtitle">围绕日常素材清理、内容二创、电商作图和本地视频处理。</p>
          </div>

          <div class="scene-grid">
            <div v-for="item in highlights" :key="item.title" class="scene-card">
              <strong>{{ item.title }}</strong>
              <span>{{ item.description }}</span>
            </div>
          </div>
        </section>

        <!-- <section class="flow-card workflow-panel">
          <div class="panel-head panel-head--workflow">
            <div class="section-kicker">使用路径</div>
            <div class="section-title">进入工具后，基本都保持同一套直达流程</div>
          </div>

          <div class="workflow-strip">
            <article v-for="step in workflowSteps" :key="step.id" class="workflow-step">
              <div class="workflow-step__index">0{{ step.id }}</div>
              <div class="workflow-step__body">
                <strong>{{ step.title }}</strong>
                <span>{{ step.description }}</span>
              </div>
            </article>
          </div>
        </section> -->
      </main>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const linkIconSvg = `
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="19" width="23" height="14" rx="7" transform="rotate(-32 10 19)" fill="#DCE7FF"/>
    <rect x="31" y="31" width="23" height="14" rx="7" transform="rotate(-32 31 31)" fill="#AFC6FF"/>
    <path d="M25 37L39 27" stroke="#1D4ED8" stroke-width="5" stroke-linecap="round"/>
    <circle cx="25" cy="37" r="3.5" fill="#1D4ED8"/>
    <circle cx="39" cy="27" r="3.5" fill="#1D4ED8"/>
  </svg>
`

const imageIconSvg = `
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="12" width="44" height="40" rx="12" fill="#D9F7F2"/>
    <rect x="16" y="18" width="32" height="28" rx="8" fill="#FFFFFF"/>
    <circle cx="24" cy="27" r="4" fill="#0F766E"/>
    <path d="M20 42L30 32L38 38L44 30L48 42H20Z" fill="#14B8A6"/>
  </svg>
`

const videoIconSvg = `
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="14" width="32" height="36" rx="12" fill="#DDEEFF"/>
    <path d="M25 24L40 32L25 40V24Z" fill="#2563EB"/>
    <path d="M46 24L56 18V46L46 40V24Z" fill="#60A5FA"/>
    <rect x="16" y="20" width="20" height="24" rx="8" stroke="#2563EB" stroke-width="3"/>
  </svg>
`

const bgIconSvg = `
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="25" cy="26" r="11" fill="#FDE7B8"/>
    <path d="M35 19H49V33" stroke="#B45309" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M49 19L23 45" stroke="#B45309" stroke-width="5" stroke-linecap="round"/>
    <path d="M18 49H46" stroke="#D97706" stroke-width="5" stroke-linecap="round"/>
    <circle cx="25" cy="26" r="5" fill="#B45309"/>
  </svg>
`

export default {
  name: 'HomePage',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      features: [
        {
          id: 'link-watermark',
          title: '链接去水印',
          description: '解析短视频分享链接，提取无水印素材、封面和音频资源，适合内容整理与二次创作。',
          buttonText: '立即体验',
          path: '/remove-watermark',
          tag: '链接解析',
          scene: '适合先做素材提取',
          tone: 'brand',
          logoTitle: 'Link Clean',
          logoSubtitle: 'Video URL',
          iconSvg: linkIconSvg
        },
        {
          id: 'ai-image',
          title: '图片去水印',
          description: '快速清理图片中的文字、路人、日期与杂物，保留更自然的背景与主体细节。',
          buttonText: '打开工具',
          path: '/ai-image-remove',
          tag: 'AI 修复',
          scene: '适合截图、海报、商品图',
          tone: 'teal',
          logoTitle: 'AI Retouch',
          logoSubtitle: 'Photo Fix',
          iconSvg: imageIconSvg
        },
        {
          id: 'video-watermark',
          title: '视频去水印',
          description: '上传本地视频并框选水印区域，处理后尽量保留清晰度与原始画面比例。',
          buttonText: '开始处理',
          path: '/video-remove',
          tag: '本地处理',
          scene: '适合剪辑前清理画面',
          tone: 'sky',
          logoTitle: 'Video Edit',
          logoSubtitle: 'Local Clip',
          iconSvg: videoIconSvg
        },
        {
          id: 'remove-bg',
          title: '人像抠图',
          description: '自动识别人物或商品主体，生成透明底图或更干净的画面，用于海报与电商素材。',
          buttonText: '开始抠图',
          path: '/remove-bg',
          tag: '主体抠图',
          scene: '适合商品图和海报素材',
          tone: 'amber',
          logoTitle: 'Cutout Pro',
          logoSubtitle: 'BG Remove',
          iconSvg: bgIconSvg
        }
      ],
      metrics: [
        {
          label: '核心工具',
          value: '4 个',
          description: '首页统一承接高频媒体处理工具'
        },
        {
          label: '进入方式',
          value: '即开即用',
          description: '减少学习成本，进入后直接开始处理'
        },
        {
          label: '覆盖内容',
          value: '视频 + 图片',
          description: '同时兼顾下载解析、修图与抠图需求'
        },
        {
          label: '操作体验',
          value: '轻量直达',
          description: '优先展示最常用的工具入口和动作'
        }
      ],
      heroPoints: [
        {
          title: '链接解析',
          description: '提取视频、封面和音频'
        },
        {
          title: '图片修复',
          description: '处理文字、水印和杂物'
        },
        {
          title: '本地视频',
          description: '框选局部区域后再处理'
        },
        {
          title: '主体抠图',
          description: '人物商品都能快速出图'
        }
      ],
      highlights: [
        {
          title: '短视频素材提取更省事',
          description: '复制分享链接即可快速解析无水印视频、封面和音频，适合内容收集、剪辑备份和二次整理。'
        },
        {
          title: '图片处理更适合日常使用',
          description: '无论是去水印、清路人、删文字，还是移除背景，都更适合电商配图、海报制作和社媒内容整理。'
        },
        {
          title: '本地视频也能直接处理',
          description: '上传视频后可针对局部区域进行去水印，适合素材二次编辑、画面清理和内容再发布前处理。'
        },
        {
          title: '打开就能开始，不需要复杂学习',
          description: '每个工具入口都保持尽量直接的操作路径，减少跳转和理解成本，进入后就能开始处理素材。'
        }
      ],
      workflowSteps: [
        {
          id: 1,
          title: '选一个入口',
          description: '按当前任务进入链接解析、图片修复、视频去水印或抠图页面。'
        },
        {
          id: 2,
          title: '上传或粘贴素材',
          description: '支持直接粘贴链接，也支持拖入图片和视频文件开始处理。'
        },
        {
          id: 3,
          title: '拿到结果并保存',
          description: '处理完成后预览结果，再按需下载、保存或继续下一步编辑。'
        }
      ]
    }
  },
  methods: {
    handleFeatureClick(feature) {
      if (feature?.path) {
        this.$router.push(feature.path)
      }
    },
    scrollToGrid() {
      this.$refs.gridRef?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.home-page {
  color: var(--ink);
}

.content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.hero-card--home {
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(79, 70, 229, 0.18), transparent 28%),
    radial-gradient(circle at 10% 80%, rgba(15, 118, 110, 0.12), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.94) 0%, rgba(248, 251, 255, 0.92) 100%);
}

.hero-shell {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.85fr);
  gap: 24px;
  align-items: stretch;
}

.hero-badge {
  display: inline-flex;
  padding: 9px 16px;
  border-radius: 999px;
  background: rgba(27, 42, 65, 0.06);
  color: var(--brand-deep);
  font-weight: 700;
  letter-spacing: 0.02em;
}

.hero-main h1 {
  margin: 18px 0 14px;
  font-size: 60px;
  line-height: 0.98;
  letter-spacing: -0.05em;
  max-width: 840px;
  color: #11233f;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}

.hero-proof {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 34px;
}

.proof-chip {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(223, 230, 242, 0.9);
}

.proof-chip strong {
  font-size: 24px;
  font-weight: 800;
  color: var(--ink);
}

.proof-chip span {
  color: var(--muted);
  font-size: 14px;
}

.hero-stage {
  display: flex;
  align-items: stretch;
}

.hero-stage__panel {
  width: 100%;
  min-height: 100%;
  padding: 26px;
  border-radius: 30px;
  background: linear-gradient(160deg, #16243f 0%, #244a90 58%, #1b9aaa 100%);
  color: #fff;
  box-shadow: 0 28px 48px rgba(23, 48, 95, 0.22);
}

.hero-stage__eyebrow {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero-stage__headline {
  margin-top: 16px;
  font-size: 40px;
  line-height: 1.05;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.hero-stage__copy {
  margin: 16px 0 0;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.85;
}

.hero-stage__mini-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 26px;
}

.hero-stage__mini-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 110px;
  padding: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
}

.hero-stage__mini-card strong {
  font-size: 18px;
}

.hero-stage__mini-card span {
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.7;
}

.panel-head {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  margin-bottom: 24px;
}

.section-kicker {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-deep);
}

.panel-head--feature {
  margin-bottom: 12px;
  padding: 0;
}

.feature-market--primary {
  padding: 28px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.94) 0%, rgba(249, 251, 255, 0.88) 100%);
}

.feature-market__title {
  margin: 0;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--ink);
  line-height: 1.15;
}

.feature-market__subtitle {
  margin: 6px 0 0;
  color: var(--muted);
  line-height: 1.7;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
}

.feature-grid--spotlight {
  margin-top: 0;
}

.feature-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 330px;
  padding: 28px;
  border-radius: 28px;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.feature-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at top right, rgba(255, 255, 255, 0.3), transparent 34%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.08), transparent 55%);
  pointer-events: none;
  z-index: 0;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 40px rgba(50, 71, 117, 0.14);
}

.feature-card--brand {
  background: linear-gradient(180deg, #f6f8ff 0%, #eef3ff 100%);
  border: 1px solid rgba(99, 102, 241, 0.14);
}

.feature-card--teal {
  background: linear-gradient(180deg, #f2fbf9 0%, #e8f8f4 100%);
  border: 1px solid rgba(20, 184, 166, 0.16);
}

.feature-card--sky {
  background: linear-gradient(180deg, #f4faff 0%, #eaf4ff 100%);
  border: 1px solid rgba(47, 128, 237, 0.14);
}

.feature-card--amber {
  background: linear-gradient(180deg, #fff9f1 0%, #fff3df 100%);
  border: 1px solid rgba(217, 119, 6, 0.14);
}

.feature-card__topline {
  display: flex;
  justify-content: flex-start;
  position: relative;
  z-index: 1;
}

.feature-card__logo {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  z-index: 1;
}

.feature-card__icon {
  width: 72px;
  height: 72px;
  border-radius: 22px;
  display: grid;
  place-items: center;
  position: relative;
  z-index: 2;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(50, 71, 117, 0.12);
}

.feature-card__icon svg {
  width: 42px;
  height: 42px;
  position: relative;
  z-index: 3;
  display: block;
  overflow: visible;
}

.feature-card__icon-graphic {
  width: 42px;
  height: 42px;
  position: relative;
  z-index: 3;
}

.feature-card__icon-graphic :deep(svg) {
  width: 42px;
  height: 42px;
  display: block;
}

.feature-card__logo-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: 1;
}

.feature-card__logo-copy strong {
  font-size: 16px;
  line-height: 1.1;
  color: var(--ink);
}

.feature-card__logo-copy span {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.feature-card--brand .feature-card__icon {
  color: #4658f3;
}

.feature-card--teal .feature-card__icon {
  color: #0f766e;
}

.feature-card--sky .feature-card__icon {
  color: #2f80ed;
}

.feature-card--amber .feature-card__icon {
  color: #d97706;
}

.feature-card__copy {
  flex: 1;
  position: relative;
  z-index: 1;
}

.feature-card__title {
  margin: 0 0 12px;
  font-size: 28px;
  line-height: 1.12;
  letter-spacing: -0.03em;
  color: var(--ink);
}

.feature-card__description {
  margin: 0;
  color: var(--muted);
  line-height: 1.9;
}

.feature-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: auto;
  position: relative;
  z-index: 1;
}

.feature-card__scene {
  font-size: 13px;
  color: var(--muted);
}

.feature-card__tag {
  display: inline-flex;
  padding: 8px 12px;
  border-radius: 999px;
  color: var(--ink);
  background: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 700;
}

.feature-card__button {
  border: none;
  border-radius: 999px;
  padding: 12px 18px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ink);
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;
}

.feature-card__button:hover {
  background: #fff;
  color: var(--brand-deep);
}

.feature-card--brand .feature-card__icon {
  background: #ffffff;
}

.feature-card--teal .feature-card__icon {
  background: #ffffff;
}

.feature-card--sky .feature-card__icon {
  background: #ffffff;
}

.feature-card--amber .feature-card__icon {
  background: #ffffff;
}

.scene-panel {
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(249, 252, 255, 0.88) 100%);
}

.panel-head--scene,
.panel-head--workflow {
  margin-bottom: 18px;
}

.scene-panel__subtitle {
  margin: 0;
  color: var(--muted);
  line-height: 1.8;
}

.scene-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.scene-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 22px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(223, 230, 242, 0.92);
  box-shadow: 0 14px 28px rgba(50, 71, 117, 0.06);
}

.scene-card strong {
  font-size: 18px;
  color: var(--ink);
}

.scene-card span {
  color: var(--muted);
  line-height: 1.8;
}

.workflow-panel {
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(20, 28, 44, 0.98) 0%, rgba(32, 52, 88, 0.95) 100%);
}

.workflow-panel .section-kicker,
.workflow-panel .section-title {
  color: #fff;
}

.workflow-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.workflow-step {
  display: flex;
  gap: 14px;
  padding: 22px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.workflow-step__index {
  min-width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-weight: 800;
}

.workflow-step__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.workflow-step__body strong {
  color: #fff;
  font-size: 18px;
}

.workflow-step__body span {
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.75;
}

@media (max-width: 1100px) {
  .hero-shell,
  .feature-grid,
  .scene-grid,
  .workflow-strip {
    grid-template-columns: 1fr;
  }

  .hero-proof {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .hero-main h1 {
    font-size: 38px;
  }

  .hero-stage__mini-grid,
  .hero-proof,
  .scene-grid,
  .workflow-strip {
    grid-template-columns: 1fr;
  }

  .feature-market--primary {
    padding: 20px;
  }

  .feature-card,
  .scene-card,
  .workflow-step {
    padding: 20px;
  }

  .feature-card__footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
